/**
 * Fast Forward: hovering any variant option (color swatch, size button, etc.) previews that
 * variant's photo in the main product gallery and shows its name next to the option label.
 * If the photo is already in the gallery, the gallery jumps to that slide (thumbnail highlights
 * too); otherwise the main image is swapped temporarily. Moving away restores the selected
 * variant; clicking selects it as usual.
 */
if (!window.__ffVariantHoverWired) {
  window.__ffVariantHoverWired = true;

  const LABEL = '.variant-option__button-label[data-ff-hover-name]';
  let active = null;

  // Compare Shopify CDN image URLs by file path only (ignore protocol, size and version params).
  const imageKey = (url) =>
    (url || '')
      .split('?')[0]
      .replace(/^https?:/, '')
      .replace(/_\d*x\d*(?=\.[a-z]+$)/i, '')
      .toLowerCase();

  const scopeFor = (label) =>
    label.closest('.shopify-section, product-information, .product-information') || document;

  const thumbRails = (slideshow) =>
    Array.from(
      slideshow.querySelectorAll('.slideshow-controls__thumbnails-container, .slideshow-controls__thumbnails')
    ).map((el) => ({ el, top: el.scrollTop, left: el.scrollLeft }));

  // Jump the gallery to a slide without letting it re-center (scroll) the thumbnail list.
  const quietSelect = async (slideshow, index) => {
    const rails = thumbRails(slideshow);
    const keep = () =>
      rails.forEach(({ el, top, left }) => {
        if (el.scrollTop !== top || el.scrollLeft !== left) el.scrollTo({ top, left, behavior: 'instant' });
      });
    try {
      await slideshow.select(index, undefined, { animate: false });
    } finally {
      keep();
      requestAnimationFrame(keep);
    }
  };

  const restore = () => {
    if (!active) return;
    const { nameEl, nameText, slideshow, prevIndex, img, src, srcset, sizes } = active;
    if (nameEl) nameEl.textContent = nameText;
    if (slideshow && slideshow.isConnected && typeof prevIndex === 'number' && slideshow.current !== prevIndex) {
      quietSelect(slideshow, prevIndex);
    }
    if (img && img.isConnected) {
      if (srcset) img.setAttribute('srcset', srcset);
      else img.removeAttribute('srcset');
      if (sizes) img.setAttribute('sizes', sizes);
      img.src = src;
    }
    active = null;
  };

  const preview = (label) => {
    if (active?.label === label) return;
    restore();

    const fieldset = label.closest('fieldset');
    const nameEl = fieldset?.querySelector('.variant-option__swatch-value, legend .variant-option__swatch-value') || null;
    active = { label, nameEl, nameText: nameEl?.textContent ?? '' };
    if (nameEl) nameEl.textContent = label.getAttribute('data-ff-hover-name') || '';

    const hoverSrc = label.getAttribute('data-ff-hover-src');
    if (!hoverSrc) return;

    const gallery = scopeFor(label).querySelector('media-gallery');
    if (!gallery) return;
    const slideshow = gallery.querySelector('slideshow-component');
    const slides = slideshow ? Array.from(slideshow.querySelectorAll('slideshow-slide')) : [];
    const wanted = imageKey(hoverSrc);
    const index = slides.findIndex((slide) => imageKey(slide.querySelector('img')?.getAttribute('src')) === wanted);

    if (slideshow && index >= 0 && typeof slideshow.select === 'function') {
      active.slideshow = slideshow;
      active.prevIndex = typeof slideshow.current === 'number' ? slideshow.current : 0;
      if (slideshow.current !== index) quietSelect(slideshow, index);
      return;
    }

    // Photo isn't in the gallery: swap the visible main image temporarily.
    const slide =
      gallery.querySelector('slideshow-slide[aria-hidden="false"]') ||
      gallery.querySelector('slideshow-slide') ||
      gallery.querySelector('.product-media-container');
    const img = slide?.querySelector('img');
    if (!(img instanceof HTMLImageElement)) return;
    active.img = img;
    active.src = img.currentSrc || img.src;
    active.srcset = img.getAttribute('srcset') || '';
    active.sizes = img.getAttribute('sizes') || '';
    const nextSrcset = label.getAttribute('data-ff-hover-srcset');
    if (nextSrcset) img.setAttribute('srcset', nextSrcset);
    else img.removeAttribute('srcset');
    img.src = hoverSrc;
  };

  // The whole group of options (all swatches incl. the gaps between them) counts as one hover area,
  // so passing over a gap keeps the last previewed option instead of flashing back.
  const groupOf = (el) => el?.closest('fieldset, .variant-option') || null;
  let restoreTimer = 0;
  const cancelRestore = () => {
    clearTimeout(restoreTimer);
    restoreTimer = 0;
  };

  document.addEventListener('pointerover', (e) => {
    if (e.pointerType !== 'mouse') return;
    const label = e.target instanceof Element ? e.target.closest(LABEL) : null;
    if (label) {
      cancelRestore();
      preview(label);
    } else if (active && e.target instanceof Element && groupOf(active.label)?.contains(e.target)) {
      cancelRestore(); // over a gap inside the same option group: keep the current preview
    }
  });

  document.addEventListener('pointerout', (e) => {
    if (e.pointerType !== 'mouse' || !active) return;
    const group = groupOf(active.label);
    if (!group || !(e.target instanceof Element) || !group.contains(e.target)) return;
    const to = e.relatedTarget instanceof Element ? e.relatedTarget : null;
    if (to && group.contains(to)) return; // still inside the swatch area (another swatch or a gap)
    cancelRestore();
    // Small grace period so brushing past the edge doesn't flicker.
    restoreTimer = window.setTimeout(restore, 120);
  });

  // Clicking chooses the variant: keep what's shown and let the theme load the new variant.
  document.addEventListener(
    'click',
    (e) => {
      const label = e.target instanceof Element ? e.target.closest(LABEL) : null;
      if (!label || !active) return;
      if (active.nameEl) active.nameEl.textContent = label.getAttribute('data-ff-hover-name') || active.nameEl.textContent;
      active = null;
    },
    true
  );
}
